import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Search, MapPin, Sparkles, Plus, AlertCircle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type YithItem = {
  name: string;
  product_id: number | null;
  bookings: number;
  suggested_studio_id: string | null;
};
export type StudioOpt = { id: string; name: string; active: boolean };
export type ProductOpt = { id: string; name: string; bookable_studio_id: string | null };

type Creds = {
  wp_url: string;
  wc_consumer_key: string;
  wc_consumer_secret: string;
};

interface Props {
  creds: Creds;
  studioMap: Record<string, string>;
  onMapChange: (m: Record<string, string>) => void;
  onItemsScanned?: (names: string[]) => void;
}

export function YithMappingPanel({ creds, studioMap, onMapChange, onItemsScanned }: Props) {
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState<YithItem[] | null>(null);
  const [studios, setStudios] = useState<StudioOpt[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [seeding, setSeeding] = useState(false);

  const reloadStudios = async () => {
    const [{ data: s }, { data: p }] = await Promise.all([
      supabase.from("studios").select("id,name,active").order("display_order"),
      supabase.from("products").select("id,name,bookable_studio_id").eq("active", true).order("display_order"),
    ]);
    setStudios((s ?? []) as StudioOpt[]);
    setProducts(((p ?? []) as ProductOpt[]).filter((x) => x.bookable_studio_id));
  };

  const scan = async () => {
    if (!creds.wp_url || !creds.wc_consumer_key || !creds.wc_consumer_secret) {
      toast.error("Enter WP URL + WooCommerce keys above first");
      return;
    }
    setScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke("wp-import", {
        body: { mode: "scan_yith", ...creds },
      });
      if (error) throw error;
      setItems(data.items ?? []);
      setStudios(data.studios ?? []);
      // Always (re)load products list for the dropdown
      const { data: p } = await supabase
        .from("products").select("id,name,bookable_studio_id").eq("active", true).order("display_order");
      setProducts(((p ?? []) as ProductOpt[]).filter((x) => x.bookable_studio_id));
      onItemsScanned?.(((data.items ?? []) as YithItem[]).map((it) => it.name));
      // Pre-fill suggestions into the map
      const next = { ...studioMap };
      for (const it of (data.items ?? []) as YithItem[]) {
        const k = it.name.toLowerCase().trim();
        if (!next[k] && it.suggested_studio_id) next[k] = `studio:${it.suggested_studio_id}`;
      }
      onMapChange(next);
      toast.success(`Found ${data.items?.length ?? 0} unique bookable products`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setScanning(false);
    }
  };

  const seedFromWp = async () => {
    if (!creds.wp_url || !creds.wc_consumer_key || !creds.wc_consumer_secret) {
      toast.error("Enter WP URL + WooCommerce keys above first");
      return;
    }
    setSeeding(true);
    try {
      const { data, error } = await supabase.functions.invoke("wp-import", {
        body: { mode: "seed_studios", ...creds },
      });
      if (error) throw error;
      const created = data?.created ?? 0;
      const skipped = (data?.skipped ?? []).length;
      toast.success(`Created ${created} studio${created === 1 ? "" : "s"} from WP scan${skipped ? ` (skipped ${skipped} existing)` : ""}`);
      await reloadStudios();
      // Re-run scan so suggestions populate
      await scan();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSeeding(false);
    }
  };

  const setOne = (name: string, studioId: string) => {
    onMapChange({ ...studioMap, [name.toLowerCase().trim()]: studioId });
  };

  const noStudios = studios.length === 0 && products.length === 0;
  const itemsWithoutMapping = (items ?? []).filter(
    (it) => !studioMap[it.name.toLowerCase().trim()]
  ).length;

  return (
    <Card className="p-6 bg-surface border-border space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm uppercase tracking-wider font-display flex items-center gap-2">
            <MapPin className="w-4 h-4" /> YITH studio mapping
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Scan WP first to see each unique bookable product, then assign it to a studio. Overrides auto-match.
          </p>
        </div>
        <Button onClick={scan} disabled={scanning} variant="outline" className="gap-2 shrink-0">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Scan WP for YITH products
        </Button>
      </div>

      {items !== null && noStudios && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">No studios found</p>
              <p className="text-xs text-muted-foreground mt-1">
                You need at least one studio before you can map YITH products to it. Seed defaults from WP, or create studios manually.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={seedFromWp} disabled={seeding} className="gap-2">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Seed studios from this WP scan
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-2">
              <Link to="/admin/studios" target="_blank" rel="noreferrer">
                <Plus className="w-4 h-4" /> Open Studios admin
              </Link>
            </Button>
          </div>
        </div>
      )}

      {items && items.length === 0 && (
        <p className="text-sm text-muted-foreground">No YITH bookable products found in orders.</p>
      )}

      {items && items.length > 0 && (
        <>
        {!noStudios && itemsWithoutMapping > 0 && (
          <div className="border border-destructive/40 bg-destructive/5 p-3 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <span>
              <strong>{itemsWithoutMapping}</strong> product{itemsWithoutMapping === 1 ? " is" : "s are"} not yet mapped to a studio.
              Bookings for unmapped products will be skipped during import.
            </span>
          </div>
        )}
        <div className="border border-border divide-y divide-border">
          <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-background text-xs uppercase tracking-wider text-muted-foreground">
            <div className="col-span-6">Product name</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-5">Maps to studio</div>
          </div>
          {items.map((it) => {
            const k = it.name.toLowerCase().trim();
            // Backward-compat: bare uuid → treat as studio
            const raw = studioMap[k] ?? "";
            const cur = raw && !raw.includes(":") ? `studio:${raw}` : raw;
            return (
              <div key={k} className="grid grid-cols-12 gap-3 px-3 py-2 items-center text-sm">
                <div className="col-span-6 truncate font-mono text-xs" title={it.name}>{it.name}</div>
                <div className="col-span-1 text-right text-muted-foreground">{it.bookings}</div>
                <div className="col-span-5">
                  <Select
                    value={cur}
                    onValueChange={(v) => setOne(it.name, v)}
                    disabled={noStudios}
                  >
                    <SelectTrigger
                      className={`h-9 ${!cur && !noStudios ? "border-destructive/50" : ""}`}
                    >
                      <SelectValue placeholder={noStudios ? "— add studios first —" : "— pick a studio or product —"} />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Studios</SelectLabel>
                          {studios.map((s) => (
                            <SelectItem key={s.id} value={`studio:${s.id}`}>
                              {s.name}{!s.active && " (inactive)"}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {products.length > 0 && (
                        <>
                          {studios.length > 0 && <SelectSeparator />}
                          <SelectGroup>
                            <SelectLabel>Products</SelectLabel>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={`product:${p.id}`}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </Card>
  );
}